import { useEffect, useState } from 'react';
import { authApi } from '../services/api';

/**
 * Staff (instructor / admin) check for preview-only UI.
 *
 * Reads `user.role` from `GET /auth/me`. It is deliberately NOT `useIsReviewer`: that hook keys off
 * `access === 'all'`, which the backend kill switch (HAURAKI_REVIEWER_ALL_ACCESS=off, the production
 * setting) turns off for everyone, staff included. This one only decides whether a harmless
 * preview is shown; it is not an authorisation boundary.
 *
 * Fetched once and cached on `window`. Any failure resolves to `false` (learner behaviour).
 */
const CACHE_KEY = '__hauraki_staff_cache__';
const STAFF_ROLES = new Set(['admin', 'instructor']);

type W = Window & { [CACHE_KEY]?: boolean };

export function isStaffRole(role: unknown): boolean {
  return typeof role === 'string' && STAFF_ROLES.has(role.toLowerCase());
}

export function useIsStaff(): boolean {
  const cached = typeof window !== 'undefined' ? (window as W)[CACHE_KEY] : undefined;
  const [staff, setStaff] = useState<boolean>(cached ?? false);

  useEffect(() => {
    if (cached !== undefined) return;
    let cancelled = false;
    authApi
      .me()
      .then((res) => {
        const role = (res.data as { user?: { role?: string } } | undefined)?.user?.role;
        const value = isStaffRole(role);
        (window as W)[CACHE_KEY] = value;
        if (!cancelled) setStaff(value);
      })
      .catch(() => {
        (window as W)[CACHE_KEY] = false;
        if (!cancelled) setStaff(false);
      });
    return () => {
      cancelled = true;
    };
    // Fetch once on mount; the window cache dedupes across consumers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return staff;
}
